// src/components/MobileBottomBar.jsx
import { useContext, useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { axiosInstance } from "../../config";
import "./mobileBottomBar.css";

const PF = process.env.REACT_APP_PUBLIC_FOLDER || '/assets/';

export default function MobileBottomBar() {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [profilePicture, setProfilePicture] = useState(null);
  const [newClips, setNewClips] = useState(() => {
    const n = Number(localStorage.getItem("cb_new_clips") || "0");
    return Number.isFinite(n) ? n : 0;
  });

  // New-clip badge on the Library tab (ForYouPage writes cb_new_clips + fires the event).
  useEffect(() => {
    const read = () => {
      const n = Number(localStorage.getItem("cb_new_clips") || "0");
      setNewClips(Number.isFinite(n) ? n : 0);
    };
    window.addEventListener("cb_new_clips", read);
    window.addEventListener("storage", read);
    return () => {
      window.removeEventListener("cb_new_clips", read);
      window.removeEventListener("storage", read);
    };
  }, []);

  useEffect(() => {
    const fetchProfilePicture = async () => {
      if (!user) return;
      try {
        const res = await axiosInstance.get("/users/me/creator-settings");
        setProfilePicture(res.data.profilePicture || null);
      } catch (err) {
        // Silently fail
      }
    };
    fetchProfilePicture();
  }, [user]);

  // Listen for profile picture updates
  useEffect(() => {
    const handleUpdate = (e) => {
      if (e.detail?.profilePicture) {
        setProfilePicture(e.detail.profilePicture);
      }
    };
    window.addEventListener("profile_picture_updated", handleUpdate);
    return () => window.removeEventListener("profile_picture_updated", handleUpdate);
  }, []);

  const tab = new URLSearchParams(location.search).get("tab");
  const onHome = location.pathname === "/home";
  const isLibraryActive = onHome && tab === "library";
  const isHomeActive = onHome && !isLibraryActive;

  const getProfilePictureUrl = () => {
    if (!profilePicture) return null;
    if (profilePicture.includes('amazonaws.com')) return null; // broken AWS URL
    if (profilePicture.startsWith("http")) return profilePicture;
    return PF + profilePicture;
  };

  return (
    <nav className="mobile-bottom-bar" role="navigation" aria-label="Bottom">
      <div
        className={"mb-tab" + (isHomeActive ? " is-active" : "")}
        onClick={() => navigate("/home")}
        data-paywall-allow=""
        role="button"
        tabIndex={0}
        aria-label={t('home', 'Home')}
      >
        <HomeIcon />
      </div>
      <div
        className={"mb-tab" + (isLibraryActive ? " is-active" : "")}
        onClick={() => navigate("/home?tab=library")}
        data-paywall-allow=""
        role="button"
        tabIndex={0}
        aria-label="Library"
      >
        <LibraryIcon />
        {newClips > 0 && <span className="mb-clip-badge">{newClips > 99 ? "99+" : newClips}</span>}
      </div>
      <NavLink
        to="/me"
        className={({ isActive }) => "mb-tab" + (isActive ? " is-active" : "")}
      >
        {getProfilePictureUrl() ? (
          <img
            src={getProfilePictureUrl()}
            alt={t('profile')}
            className="mb-profile-picture"
          />
        ) : (
          <UserIcon />
        )}
      </NavLink>
    </nav>
  );
}

/* ---- icons ---- */
function HomeIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" />
    </svg>
  );
}

function LibraryIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}

/* ---- icons ---- */
function ClipsIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M2 8h20" />
      <path d="M8 4v4" />
      <path d="M16 4v4" />
    </svg>
  );
}

function CampaignsIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
      <line x1="12" y1="12" x2="12" y2="16" />
      <line x1="10" y1="14" x2="14" y2="14" />
    </svg>
  );
}

function CalendarIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function UserIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21a8 8 0 10-16 0" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
